import MetricTypes from "./metric-types";
import KPIImpactFormats from "./kpi-impact-formats";
import NumberParser from "./NumberParser";
import { useStringTemplate } from "../components/StringProvider";
import { IntlMessageFormat } from "intl-messageformat";
import { useSetting } from "../components/SettingsProvider";

interface FormatOptions {
  locale?: string;
  currency?: string;
  notation?: "standard" | "compact";
  showPlusSign?: boolean;
  showUnits?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  currencyDisplay?: "narrowSymbol" | "symbol" | "code" | "name";
  customImpactFormat?: string;
  trim?: boolean;
}

interface StringTemplatePlaceholders {
  [key: string]: string | number;
}

const deleteNullOrUndefinedField = (
  data: Record<string, any>,
  field: string
): void => {
  if (data[field] === null || data[field] === undefined) {
    delete data[field];
  }
};

const useDateTimeFormat = (date: string | Date): string => {
  const locale = useSetting("locale");
  const d = new Date(date);
  const month = new Intl.DateTimeFormat(locale, { month: "short" }).format(d);
  const day = new Intl.DateTimeFormat(locale, { day: "2-digit" }).format(d);

  return `${month} ${day} ${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
};

// Deprecated: Use useDateTimeFormat instead
const dateTimeFormat = useDateTimeFormat;

const useDateTimeYearFormat = (date: string | Date): string => {
  const locale = useSetting("locale");
  const d = new Date(date);
  const year = new Intl.DateTimeFormat(locale, { year: "numeric" }).format(d);
  const month = new Intl.DateTimeFormat(locale, { month: "short" }).format(d);
  const day = new Intl.DateTimeFormat(locale, { day: "numeric" }).format(d);

  return `${day} ${month}, ${year}, ${String(d.getHours()).padStart(
    2,
    "0"
  )}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// Deprecated: Use useDateTimeYearFormat instead
const dateTimeYearFormat = useDateTimeYearFormat;

const useTimeSince = (createdAt: string | Date): string => {
  const d = new Date(createdAt);
  const seconds = Math.floor((new Date().getTime() - d.getTime()) / 1000);
  const locale = useSetting("locale");

  if (seconds > 60 * 60 * 24) {
    const month = new Intl.DateTimeFormat(locale, { month: "short" }).format(d);
    const day = new Intl.DateTimeFormat(locale, { day: "2-digit" }).format(d);

    return `${month} ${day} ${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}`;
  }

  if (seconds > 60 * 60) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }

  if (seconds > 60) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  }

  return `${seconds} ${seconds === 1 ? "second" : "seconds"} ago`;
};

// Deprecated: Use useTimeSince instead
const timeSince = useTimeSince;

const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (!+bytes) return "0 Bytes";

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const formatPercentage = ({
  value,
  options = {}
}: {
  value: number | null | undefined;
  options?: FormatOptions;
}): string => {
  const { showPlusSign } = options;

  if (value === null || value === undefined) {
    return "";
  }

  return value >= 0 ? `${showPlusSign ? "+" : ""}${value}%` : `${value}%`;
};

const formatPercentageWithDP = ({
  value,
  options = {}
}: {
  value: number | null | undefined;
  options?: FormatOptions;
}): string => {
  const { locale } = options;
  const { showPlusSign } = options;
  const format = new Intl.NumberFormat(locale, {
    style: "decimal",
    notation: "standard",
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits
  }).format;

  if (value === null || value === undefined) {
    return "";
  }

  return value >= 0
    ? `${showPlusSign ? "+" : ""}${format(value)}%`
    : `${format(value)}%`;
};

const formatPercentage1DP = ({
  value,
  options = { minimumFractionDigits: 1, maximumFractionDigits: 1 }
}: {
  value: number | null | undefined;
  options?: FormatOptions;
}): string => {
  return formatPercentageWithDP({ value, options });
};

const formatNumber = ({
  value,
  options
}: {
  value: number;
  options: FormatOptions;
}): string => {
  const { locale, notation = "standard" } = options;
  const { minimumFractionDigits } = options;
  const format = new Intl.NumberFormat(locale, {
    notation,
    minimumFractionDigits
  }).format;

  return format(value);
};

const applyStringTemplate = ({
  template,
  placeholders
}: {
  template: string;
  placeholders: StringTemplatePlaceholders;
}): string => {
  return new IntlMessageFormat(template, "en-US").format(
    placeholders
  ) as string;
};

const formatMaxDP = ({
  value,
  locale,
  maximumFractionDigits = 1,
  notation = "standard"
}: {
  value: number;
  locale?: string;
  maximumFractionDigits?: number;
  notation?: "standard" | "compact";
}): string => {
  const numberFormat = new Intl.NumberFormat(locale, {
    style: "decimal",
    notation,
    maximumFractionDigits
  }).format;

  return numberFormat(value);
};

const useFormatUnit = ({
  value,
  options = {},
  unitType
}: {
  value: number | null | undefined;
  options?: FormatOptions;
  unitType: string;
}): string => {
  const { showPlusSign, showUnits, locale, notation = "standard" } = options;

  // Call hooks at the top level, before any early returns
  const unitTemplates: { [key: string]: string } = {};
  unitTemplates[KPIImpactFormats.FTE] = useStringTemplate(
    "formats.FTE",
    "{value} FTE"
  );
  unitTemplates[KPIImpactFormats.HOURS] = useStringTemplate(
    "formats.Hours",
    "{value} Hours"
  );
  unitTemplates[KPIImpactFormats.WEEKS] = useStringTemplate(
    "formats.Weeks",
    "{value} Weeks"
  );
  unitTemplates[KPIImpactFormats.PERCENTAGE_POINTS] = useStringTemplate(
    "formats.PercentagePoints",
    "{value} pp"
  );

  if (value === null || value === undefined) {
    return "";
  }

  const valueDP = formatMaxDP({ value, locale, notation });

  const _unit = applyStringTemplate({
    template: unitTemplates[unitType],
    placeholders: { value: valueDP }
  });

  const valueUnit = showUnits ? " " + _unit : valueDP;

  return value >= 0 ? `${showPlusSign ? "+" : ""}${valueUnit}` : valueUnit;
};

// Deprecated: Use useFormatUnit instead
const formatUnit = useFormatUnit;

const formatCustom = ({
  value,
  options = {}
}: {
  value: number | null | undefined;
  options?: FormatOptions;
}): string => {
  const {
    showPlusSign,
    showUnits,
    customImpactFormat,
    notation = "standard",
    locale
  } = options;

  if (value === null || value === undefined) {
    return "";
  }

  const valueDP = formatMaxDP({
    value,
    locale,
    maximumFractionDigits: 0,
    notation
  });

  return `${showPlusSign ? "+" : ""}${
    !showUnits
      ? valueDP
      : customImpactFormat
      ? customImpactFormat.replace("{impact}", valueDP)
      : valueDP
  }`;
};

const formatRangeNumber = ({
  value,
  type,
  options
}: {
  value: number;
  type: string;
  options: FormatOptions;
}): string => {
  switch (type) {
    case MetricTypes.PERCENTAGE:
      return Number.isInteger(value)
        ? formatPercentage({ value, options })
        : formatPercentage1DP({ value, options });
    case MetricTypes.PERCENTAGE1DP:
      return formatPercentage1DP({ value, options });
    case MetricTypes.CURRENCY:
      return formatCurrencyString({ value, settings: options, options });
    case MetricTypes.NUMBER2DP:
      return formatNumber2DP({ value, options });
    case KPIImpactFormats.MULTIPLIER:
      return formatMultiplier({ value, options });
    case KPIImpactFormats.FTE:
    case KPIImpactFormats.HOURS:
    case KPIImpactFormats.WEEKS:
    case KPIImpactFormats.PERCENTAGE_POINTS:
      return formatUnit({
        value,
        options,
        unitType: type
      });
    case KPIImpactFormats.NUMBER:
      return formatNumber({ value, options });
    case KPIImpactFormats.CUSTOM:
      return formatCustom({
        value,
        options
      });
    default:
      return String(value);
  }
};

const formatMetric = ({
  type,
  value,
  options
}: {
  type: string;
  value: number;
  options: FormatOptions;
}): string => {
  switch (type) {
    case "Currency":
      return formatCurrencyString({ value, settings: options, options });
    case "Percentage":
      return `${value}%`;
    default:
      return formatNumber({
        value,
        options
      });
  }
};

const formatMultiplier = ({
  value,
  options
}: {
  value: number;
  options: FormatOptions;
}): string => {
  const { locale } = options;
  const format = new Intl.NumberFormat(locale, {
    style: "decimal",
    notation: "standard",
    maximumFractionDigits: 1
  }).format;
  return `${format(value / 100)}x`;
};

const formatNumber2DP = ({
  value,
  options
}: {
  value: number;
  options: FormatOptions;
}): string => {
  const { locale } = options;
  const format = new Intl.NumberFormat(locale, {
    style: "decimal",
    notation: "standard",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format;
  return format(value);
};

const formatCurrencyString = ({
  value,
  settings = {},
  options = {}
}: {
  value: number;
  settings?: FormatOptions;
  options?: FormatOptions;
}): string => {
  const { locale, currency = "GBP", trim = true } = settings;
  const {
    notation = "standard",
    currencyDisplay = "narrowSymbol",
    minimumFractionDigits,
    maximumFractionDigits
  } = options;

  const format = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation,
    currencyDisplay,
    minimumFractionDigits,
    maximumFractionDigits
  }).format;
  const result = format(value);

  if (!trim) {
    return result;
  }

  const decimalSymbol = getDecimalSymbol({ settings });
  const uselessTrailingDecimals = `${decimalSymbol}00`;
  return result.replace(uselessTrailingDecimals, "");
};

const getCurrencySymbol = ({
  settings = {}
}: {
  settings?: FormatOptions;
}): string => {
  const { locale, currency = "GBP" } = settings;
  const notation = "compact";
  const currencyDisplay = "narrowSymbol";
  const format = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation,
    currencyDisplay
  }).format;
  const result = format(0);
  return result.replaceAll(/\d/g, "");
};

const getDecimalSymbol = ({
  settings = {}
}: {
  settings?: FormatOptions;
}): string => {
  const { locale, currency = "GBP" } = settings;
  const notation = "compact";
  const currencyDisplay = "narrowSymbol";
  const format = new Intl.NumberFormat(locale, {
    style: "decimal",
    currency,
    notation,
    currencyDisplay
  }).format;
  const result = format(1.55);

  return result.replaceAll(/\d/g, "");
};

const isNumber = (value: unknown): boolean => {
  return !isNaN(value as number) && !isNaN(parseFloat(value as string));
};

const getPercentageFormatter = () => (value: number | string | undefined) =>
  value !== "" && value !== undefined
    ? formatPercentage({
        value: Number(value)
      })
    : value;

const getRangeNumberFormatter =
  ({ type, options }: { type: string; options: FormatOptions }) =>
  (value: number) =>
    formatRangeNumber({
      value,
      type,
      options
    });

const getMetricFormatter =
  ({
    locale,
    currency,
    type,
    options = { notation: "compact" }
  }: {
    locale?: string;
    currency?: string;
    type: string;
    options?: FormatOptions;
  }) =>
  (value: number | string): string => {
    options["locale"] = locale;
    options["currency"] = currency;

    if (!isNumber(value)) {
      return "";
    }

    return formatMetric({ type, value: Number(value), options });
  };

const getMetricParser =
  ({ locale, currency }: { locale?: string; currency?: string }) =>
  (value: string): number | string => {
    const parser = new NumberParser(locale, currency);
    const currencySymbol = getCurrencySymbol({
      settings: { locale, currency }
    });
    const trimmedValue = value
      .replace("%", "")
      .replace(currencySymbol, "")
      .trim();

    return trimmedValue ? parser.parse(trimmedValue) : value;
  };

const getMetricTooltipFormatter =
  ({
    locale,
    currency,
    type
  }: {
    locale?: string;
    currency?: string;
    type: string;
  }) =>
  (value: number): string => {
    switch (type) {
      case "Currency":
        return formatCurrencyString({ value, settings: { locale, currency } });
      case "Percentage":
        return formatPercentage({ value });
      default:
        return formatNumber({ value, options: { locale } });
    }
  };

const getMetricMarkFormatter =
  ({
    locale,
    currency,
    type
  }: {
    locale?: string;
    currency?: string;
    type: string;
  }) =>
  (value: number): string => {
    switch (type) {
      case "Currency":
        return formatCurrencyString({
          value,
          settings: { locale, currency },
          options: { notation: "compact" }
        });
      case "Percentage":
        return formatPercentage({ value });
      default:
        return formatNumber({
          value,
          options: { locale, notation: "compact" }
        });
    }
  };

const getCurrencyFormatter =
  ({
    settings,
    options
  }: {
    settings: FormatOptions;
    options?: FormatOptions;
  }) =>
  (value: number | string | undefined): string | number | undefined => {
    return value !== "" && value !== undefined
      ? formatCurrencyString({ value: Number(value), settings, options })
      : value;
  };

const formatROIPercentage = ({
  value,
  options
}: {
  value: number;
  options: FormatOptions;
}): string => {
  if (value > 200) {
    const { locale } = options;
    const format = new Intl.NumberFormat(locale, {
      style: "decimal",
      notation: "standard",
      maximumFractionDigits: 1
    }).format;

    return value > 1000
      ? `${format(Math.round(value / 100))}x`
      : `${format(value / 100)}x`;
  }

  return formatPercentage({ value });
};

const formatDate = ({
  time,
  locale,
  separator = "/"
}: {
  time: string | Date;
  locale?: string;
  separator?: string;
}): string => {
  const date = new Date(time);

  return date
    .toLocaleDateString(locale, {
      year: "numeric",
      month: "numeric",
      day: "numeric"
    })
    .replaceAll("/", separator);
};

const formatConfigurationDetailDate = ({
  time,
  locale
}: {
  time: string | Date;
  locale?: string;
}): string => {
  const date = new Date(time);

  return date.toLocaleDateString(locale, {
    day: "numeric",
    year: "numeric",
    month: "short"
  });
};

const formatNumberWithLocale = ({
  value,
  locale
}: {
  value?: number;
  locale?: string;
}): string | undefined => {
  return value !== undefined ? value.toLocaleString(locale) : undefined;
};

export {
  formatPercentage,
  formatPercentageWithDP,
  deleteNullOrUndefinedField,
  timeSince,
  formatBytes,
  formatNumber,
  formatRangeNumber,
  formatMetric,
  formatMultiplier,
  formatNumber2DP,
  formatCurrencyString,
  getCurrencySymbol,
  getPercentageFormatter,
  getRangeNumberFormatter,
  getMetricMarkFormatter,
  getMetricTooltipFormatter,
  getMetricFormatter,
  getCurrencyFormatter,
  getMetricParser,
  formatROIPercentage,
  formatDate,
  formatConfigurationDetailDate,
  formatCustom,
  applyStringTemplate,
  formatNumberWithLocale,
  dateTimeFormat,
  dateTimeYearFormat,
  // Custom hooks (use these instead of the deprecated non-hook versions)
  useDateTimeFormat,
  useDateTimeYearFormat,
  useTimeSince,
  useFormatUnit
};
